import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Bell, Warning, Clock, CurrencyDollar, Buildings, FileText, Printer, CalendarBlank } from "@phosphor-icons/react";

const fmt = (n) => (!n && n !== 0) ? "$0" : `$${Number(n).toLocaleString()}`;
const urgStyle = { overdue: "border-[#FF2A00]/30 bg-[#FF2A00]/5", urgent: "border-[#FFD100]/30 bg-[#FFD100]/5", upcoming: "border-[#002FA7]/20 bg-[#002FA7]/5" };

export default function DigestPreview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/digest/preview").then((r) => setData(r.data)).catch(() => toast.error("Failed to load digest")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 flex items-center justify-center h-full"><p className="text-sm text-[#6B7280]">Generating digest...</p></div>;
  if (!data) return null;

  const { summary, overdue, urgent, upcoming, period } = data;

  const DeadlineRow = ({ item }) => (
    <tr className="border-b border-gray-100">
      <td className="py-2 px-3 text-sm text-[#1A1D20]">{item.title}</td>
      <td className="py-2 px-3 text-sm text-[#6B7280]">{item.client_name}</td>
      <td className="py-2 px-3 text-sm">{item.deadline_date}</td>
      <td className="py-2 px-3 text-sm font-semibold" style={{ color: item.days_remaining < 0 ? "#FF2A00" : item.days_remaining <= 30 ? "#946B00" : "#002FA7" }}>
        {item.days_remaining < 0 ? `${Math.abs(item.days_remaining)}d overdue` : `${item.days_remaining}d left`}
      </td>
    </tr>
  );

  return (
    <div className="p-6" data-testid="digest-preview-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Notifications</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Digest Preview</h1>
          <p className="text-sm text-[#6B7280] mt-1">Weekly deadline summary — preview what an email digest would contain</p>
        </div>
        <button data-testid="print-digest-btn" onClick={() => window.print()} className="flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm transition-colors">
          <Printer size={16} /> Print
        </button>
      </div>

      {/* Digest content */}
      <div className="max-w-3xl mx-auto bg-white border border-gray-200" data-testid="digest-content">
        {/* Header */}
        <div className="bg-[#002FA7] px-8 py-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 bg-white/20 flex items-center justify-center"><Bell size={18} className="text-white" weight="bold" /></div>
            <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>E-Rate CRM Deadline Digest</h2>
          </div>
          <p className="text-sm text-white/70">{period}</p>
        </div>

        {/* Alert summary */}
        <div className="grid grid-cols-3 gap-0 border-b border-gray-200">
          <div className="p-5 border-r border-gray-200 text-center">
            <p className="text-2xl font-bold text-[#FF2A00]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{summary.overdue_count}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mt-1">Overdue</p>
          </div>
          <div className="p-5 border-r border-gray-200 text-center">
            <p className="text-2xl font-bold text-[#946B00]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{summary.urgent_count}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mt-1">Urgent</p>
          </div>
          <div className="p-5 text-center">
            <p className="text-2xl font-bold text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{summary.upcoming_count}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mt-1">Upcoming</p>
          </div>
        </div>

        {/* Portfolio snapshot */}
        <div className="px-8 py-5 border-b border-gray-200">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-3">Portfolio Snapshot</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-2"><Buildings size={16} className="text-[#002FA7]" /><div><p className="text-lg font-bold text-[#1A1D20]">{summary.total_clients}</p><p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Clients</p></div></div>
            <div className="flex items-center gap-2"><FileText size={16} className="text-[#002FA7]" /><div><p className="text-lg font-bold text-[#1A1D20]">{summary.active_contracts}</p><p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Active Contracts</p></div></div>
            <div className="flex items-center gap-2"><CurrencyDollar size={16} className="text-[#002FA7]" /><div><p className="text-lg font-bold text-[#1A1D20]">{fmt(summary.total_committed)}</p><p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Committed</p></div></div>
          </div>
        </div>

        {/* Deadlines sections */}
        {overdue.length > 0 && (
          <div className="px-8 py-5 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Warning size={16} className="text-[#FF2A00]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#FF2A00]">Overdue ({overdue.length})</p>
            </div>
            <table className="w-full text-left">
              <tbody>{overdue.map((d, i) => <DeadlineRow key={i} item={d} />)}</tbody>
            </table>
          </div>
        )}

        {urgent.length > 0 && (
          <div className="px-8 py-5 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-[#946B00]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#946B00]">Due Within 30 Days ({urgent.length})</p>
            </div>
            <table className="w-full text-left">
              <tbody>{urgent.map((d, i) => <DeadlineRow key={i} item={d} />)}</tbody>
            </table>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="px-8 py-5 border-b border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <CalendarBlank size={16} className="text-[#002FA7]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">Upcoming ({upcoming.length})</p>
            </div>
            <table className="w-full text-left">
              <tbody>{upcoming.map((d, i) => <DeadlineRow key={i} item={d} />)}</tbody>
            </table>
          </div>
        )}

        {overdue.length === 0 && urgent.length === 0 && upcoming.length === 0 && (
          <div className="px-8 py-12 text-center">
            <CalendarBlank size={32} className="mx-auto text-[#00B341] mb-3" />
            <p className="text-sm font-medium text-[#1A1D20]">All clear!</p>
            <p className="text-xs text-[#6B7280] mt-1">No deadlines require attention this week.</p>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-4 bg-[#F8F9FA] text-center">
          <p className="text-[10px] text-[#9CA3AF] uppercase tracking-[0.15em]">E-Rate CRM Consultant Tool — Generated {data.generated_at}</p>
        </div>
      </div>
    </div>
  );
}
